import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../store/hooks';
import { useIsAdmin } from '../lib/auth/useIsAdmin';

/**
 * Route guard for `/admin/*` surfaces.
 *
 * Renders children only when the current visitor is authenticated
 * AND their Cognito ID token carries the `admin` group claim. The
 * server-side admin-group check inside every admin handler
 * (`services/src/api-layer/managers/require-admin-group.ts`) is the
 * real security boundary — this guard is a UX affordance so
 * non-admins do not see broken admin surfaces (and to keep the
 * admin routes discoverable but inaccessible without out-of-band
 * grant per `docs/user-management.md`).
 *
 * Redirects:
 *   - Unauthenticated visitors → `/sign-in?next=<current path>`.
 *   - Authenticated non-admins → `/` (the "you are not an admin"
 *     branch is deliberately silent — 404 semantics is the standard
 *     convention for "gate you cannot see past").
 */
export function RequireAdmin({ children }: { readonly children: ReactNode }): JSX.Element {
  const isAuthenticated = useAppSelector((state) => state.auth.status === 'authenticated');
  const isAdmin = useIsAdmin();
  const location = useLocation();

  if (!isAuthenticated) {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/sign-in?next=${next}`} replace />;
  }
  if (!isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}
